/**
 * reservationModule.js
 * 2026-02-10: alterReservation sets global loading and rethrows errors
 */
import axios from '@/axios/config'
import { capitalize } from 'lodash'

export default {
  namespaced: true,
  state: {
    list: [],
    channels: [],
  },
  mutations: {
    setReservations(state, payload) {
      state.list = payload
    },
    setChannels: (state, channels) => (state.channels = channels),
  },
  getters: {
    channelsItems: state =>
      state.channels.map(channel => ({
        text: capitalize(channel),
        value: channel,
      })),
  },
  actions: {
    async alterReservation(store, { id, ...payload }) {
      store.commit('updateLoading', true, { root: true })
      try {
        const { data } = await axios.put(`/api/reservations/${id}`, payload)
        return data
      } catch (err) {
        console.error(err)
        throw err
      } finally {
        store.commit('updateLoading', false, { root: true })
      }
    },
    async updateReservation({ commit }, { id, ...payload }) {
      commit('updateListingLoading', true, { root: true })
      try {
        const { data } = await axios.put(
          `/api/reservations/${id}/info`,
          payload
        )
        commit('updateListingLoading', false, { root: true })
        return data
      } catch (err) {
        commit('updateListingLoading', false, { root: true })
        console.error(err)
      }
    },
    async createReservation({ commit }, payload) {
      try {
        commit('updateLoading', true, { root: true })
        const { data } = await axios.post(`/api/reservations/`, payload)
        commit('updateLoading', false, { root: true })
        return data
      } catch (err) {
        commit('updateLoading', false, { root: true })
        console.error(err)
      }
      return {}
    },
    async createInternalReservation({ commit }, payload) {
      try {
        commit('updateLoading', true, { root: true })
        const { data } = await axios.post(`/api/reservations/internal`, payload)
        commit('updateLoading', false, { root: true })
        return data
      } catch (err) {
        commit('updateLoading', false, { root: true })
        console.error(err)
      }
      return {}
    },
    async updateInternalReservation({ commit }, { id, ...payload }) {
      try {
        commit('updateLoading', true, { root: true })
        const { data } = await axios.post(
          `/api/reservations/${id}/update`,
          payload
        )
        commit('updateLoading', false, { root: true })
        return data
      } catch (err) {
        commit('updateLoading', false, { root: true })
        console.error(err)
      }
      return {}
    },
    async getReservation({ commit }, { id, include_cal, refresh }) {
      const { data } = await axios.get(`/api/reservations/${id}`, {
        params: { include_cal, refresh },
      })
      commit('setCurrentListingCalendar', { data }, { root: true })
      return data
    },
    async getReservations({ commit }, date) {
      try {
        const { data } = await axios.get('/api/reservations', {
          params: {
            date,
          },
        })
        commit('setReservations', data)
      } catch (err) {
        console.log(err)
      }
    },
    async searchReservations(store, { term }) {
      const { data } = await axios.get('/api/conversations/search', {
        params: { term },
      })
      return data
    },
    async calculateTaxAmount(store, { reservation_id, tax_id }) {
      const { data } = await axios.post(
        `/api/reservation/${reservation_id}/calculate-tax-amount`,
        { tax_id }
      )
      return data
    },
    async applyApplicationVat(
      store,
      { reservation_id, item_ids, inclusive, vat_rate, amount_override }
    ) {
      const { data } = await axios.post(
        `/api/reservation/${reservation_id}/apply-application-vat`,
        { item_ids, inclusive, vat_rate_override: vat_rate, amount_override }
      )
      return data
    },
    async addInvoice(store, { reservation_id, ...data }) {
      return axios.post(`/api/reservation/${reservation_id}/add-invoice`, data)
    },
    async editCanceledInvoiceItem(store, { reservation_id, index, item }) {
      const { data } = await axios.patch(
        `/api/reservation/${reservation_id}/edit-canceled-invoice-item`,
        { index, item }
      )
      return data
    },
    async removeCanceledInvoiceItem(store, { reservation_id, invoice }) {
      const { data } = await axios.post(
        `/api/reservation/${reservation_id}/remove-canceled-invoice-item`,
        { invoice }
      )
      return data
    },
    async emailInvoice(_, { id, ...data }) {
      return axios.post(`/api/reservations/${id}/email-invoice`, data)
    },
    async confirmAgentReservation(_, payload) {
      return axios.post(`/api/reservations/confirm-agent-reservation`, payload)
    },
    async fetchChannels({ commit }) {
      try {
        const response = await axios.get('/api/reservations/channels')
        const channels = response.data.payload.data.channels
        commit('setChannels', channels)
      } catch (error) {
        console.error('Error fetching channels:', error)
      }
    },
    async fetchVerificationDetails({ _commit }, { reservation_id }) {
      try {
        const response = await axios.get(
          '/api/reservations/verification_details',
          {
            params: { reservation_id },
          }
        )
        return response.data.payload.data.verification_details
      } catch (error) {
        console.error('Error fetching channels:', error)
      }
    },
    async generateBackupDoorCodes({ _commit }, { reservation_id }) {
      try {
        await axios.get('/api/reservations/generate-backup-codes', {
          params: { reservation_id },
        })
      } catch (error) {
        console.error('Error fetching channels:', error)
      }
    },
    async generateDoorCodes(_store, { reservation_id }) {
      try {
        await axios.post('/api/reservations/generate-door-codes', {
          reservation_id,
        })
      } catch (error) {
        console.error('Error generating door codes:', error)
      }
    },
    async generateLivionkeyKeysContracts(_store, { reservation_id }) {
      try {
        await axios.post(
          '/api/reservations/generate-livionkey-keys-contracts',
          {
            reservation_id,
          }
        )
      } catch (error) {
        console.error('Error generating Livionkey keys contracts:', error)
      }
    },
    async cancelLivionkeyContracts(_store, { reservation_id }) {
      try {
        await axios.post('/api/reservations/cancel-livionkey-contracts', {
          reservation_id,
        })
      } catch (error) {
        console.error('Error cancelling Livionkey contracts:', error)
      }
    },
    async getLivionkeyContractDetails(_store, { reservation_id }) {
      try {
        const { data } = await axios.get(
          '/api/reservations/livionkey-contract-details',
          {
            params: { reservation_id },
          }
        )
        return data
      } catch (error) {
        console.error('Error fetching Livionkey contract details:', error)
        return null
      }
    },
    async getRawRuData(_, { id }) {
      const { data } = await axios.get(`/api/reservations/${id}/raw_ru_data`)
      return data
    },
    async resyncReservation(_, { id }) {
      const { data } = await axios.get(`/api/reservations/${id}/resync`)
      return data
    },
    async setUnsync(_, { id }) {
      const { data } = await axios.post(`/api/reservations/${id}/set-unsync`)
      return data
    },
    async releaseUnsync(_, { id }) {
      const { data } = await axios.post(
        `/api/reservations/${id}/release-unsync`
      )
      return data
    },
    async getCancellationPreview(_, { id }) {
      const { data } = await axios.get(
        `/api/reservations/${id}/cancellation-preview`
      )
      return data
    },
    async cancelReservation(
      _,
      { id, mode, canceled_by, final_income, cancellation_reason }
    ) {
      const { data } = await axios.post(`/api/reservations/${id}/cancel`, {
        mode,
        canceled_by,
        final_income,
        cancellation_reason,
      })
      return data
    },
    async adjustCanceledIncome(_, { id, canceled_income }) {
      const { data } = await axios.patch(
        `/api/reservations/${id}/adjust-canceled-income`,
        { canceled_income }
      )
      return data
    },
  },
}
